<template>
  <div class="p-4">
    <h2 class="text-2xl font-semibold mb-2">Engagement Analytics</h2>
    <p class="text-gray-600 mb-4">At-a-glance engagement across your university.</p>
    <div v-if="!authorized" class="text-red-600">You do not have access to analytics.</div>
    <div v-else>
      <div class="mb-6 flex flex-wrap gap-4 items-end">
        <!-- University selector (match OrdersAnalytics style) -->
        <div>
          <label class="block text-sm text-gray-600 mb-1">University</label>
          <template v-if="allowedUniversities.length > 1">
            <select v-model="selectedSlug" class="border rounded px-3 py-2 min-w-[260px]" @change="onUniversityChange">
              <option v-if="allowedUniversities.length > 1" value="_ALL_">All my universities</option>
              <option v-for="u in allowedUniversities" :key="u.slug" :value="u.slug">{{ u.name }} ({{ u.slug }})</option>
            </select>
          </template>
          <template v-else>
            <div class="px-3 py-2 rounded bg-gray-50 border inline-block min-w-[240px]">{{ allowedUniversities[0]?.name || selectedSlug }} ({{ selectedSlug }})</div>
          </template>
        </div>
      </div>

      <!-- Live (last 30s) -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-lg font-semibold">Live</h3>
          <div class="flex items-center gap-3">
            <div class="text-xs text-gray-500">Auto-refreshing every 5s</div>
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Window</label>
              <div class="flex gap-1">
                <button v-for="w in liveWindows" :key="w" @click="setLiveWindow(w)" :class="['px-2 py-1 rounded border text-xs', activeLiveWindow===w ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']">{{ formatWindow(w) }}</button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="liveLoading" class="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
          <div class="rounded-xl p-5 border bg-white">
            <div class="h-3 w-24 bg-gray-200 rounded mb-3"></div>
            <div class="h-7 w-20 bg-gray-300 rounded"></div>
          </div>
          <div class="rounded-xl p-5 border bg-white">
            <div class="h-3 w-28 bg-gray-200 rounded mb-3"></div>
            <div class="h-7 w-20 bg-gray-300 rounded"></div>
          </div>
          <div class="rounded-xl p-5 border bg-white">
            <div class="h-3 w-28 bg-gray-200 rounded mb-3"></div>
            <div class="h-7 w-20 bg-gray-300 rounded"></div>
          </div>
        </div>
        <div v-else-if="liveError" class="text-red-600 text-sm">{{ liveError }}</div>
        <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Active users</div>
            <div class="text-3xl font-semibold">{{ liveStats?.totals?.active_users || 0 }}</div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm mb-2">Top pages</div>
            <div v-if="!liveStats?.by_page?.length" class="text-sm text-gray-500">No data</div>
            <div v-else>
              <div v-for="row in liveStats.by_page.slice(0,5)" :key="row.path" class="text-sm flex justify-between mb-1">
                <span class="truncate max-w-[220px]" :title="row.path">{{ row.path }}</span>
                <span>{{ row.users }}</span>
              </div>
            </div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm mb-2">Top countries</div>
            <div v-if="!liveStats?.by_country?.length" class="text-sm text-gray-500">No data</div>
            <div v-else>
              <div v-for="row in liveStats.by_country.slice(0,5)" :key="row.country" class="text-sm flex justify-between mb-1">
                <span>{{ row.country }}</span>
                <span>{{ row.users }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Historical section header + hint + controls -->
      <div class="flex items-center justify-between mt-6 mb-3">
        <h3 class="text-lg font-semibold">Historical</h3>
        <div class="flex items-center gap-4">
          <div v-if="isAllScope" class="text-xs text-gray-500">Historical shown per university. Select a university to view.</div>
          <template v-else>
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Time range</label>
              <div class="flex gap-1">
                <button v-for="p in presets" :key="p.key" @click="applyPreset(p.key)" :class="['px-2 py-1 rounded border text-xs', p.key===activePreset ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']">{{ p.label }}</button>
                <button @click="toggleCustom=!toggleCustom" class="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs">Custom</button>
              </div>
              <div v-if="toggleCustom" class="flex items-end gap-2 ml-2">
                <div>
                  <label class="block text-xs text-gray-600 mb-1">From</label>
                  <input v-model="customFrom" type="datetime-local" class="border rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">To</label>
                  <input v-model="customTo" type="datetime-local" class="border rounded px-2 py-1 text-sm" />
                </div>
                <button @click="applyCustom" class="px-3 py-2 rounded bg-black text-white text-xs">Apply</button>
              </div>
              <button @click="load" class="ml-2 bg-black text-white px-3 py-2 rounded text-xs">Refresh</button>
            </div>
          </template>
        </div>
      </div>

      <div v-if="loading">
        <!-- Skeleton: KPI cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-pulse">
          <div v-for="i in 6" :key="'sk-card-'+i" class="rounded-xl p-5 border bg-white">
            <div class="h-3 w-24 bg-gray-200 rounded mb-3"></div>
            <div class="h-7 w-20 bg-gray-300 rounded"></div>
          </div>
        </div>
        <!-- Skeleton: two panels (share + tz) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
          <div>
            <div class="h-4 w-40 bg-gray-200 rounded mb-4"></div>
            <div v-for="i in 4" :key="'sk-share-'+i" class="mb-3">
              <div class="flex justify-between mb-2">
                <div class="h-3 w-24 bg-gray-200 rounded"></div>
                <div class="h-3 w-10 bg-gray-200 rounded"></div>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 bg-gray-200 rounded-full" style="width: 60%"></div>
              </div>
            </div>
          </div>
          <div>
            <div class="h-4 w-32 bg-gray-200 rounded mb-4"></div>
            <div class="h-3 w-52 bg-gray-100 rounded mb-3"></div>
            <div v-for="i in 4" :key="'sk-tz-'+i" class="mb-3">
              <div class="flex justify-between mb-2">
                <div class="h-3 w-28 bg-gray-200 rounded"></div>
                <div class="h-3 w-8 bg-gray-200 rounded"></div>
              </div>
              <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-2 bg-gray-200 rounded-full" style="width: 45%"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- Skeleton: top sources -->
        <div class="mt-8 animate-pulse">
          <div class="h-4 w-36 bg-gray-200 rounded mb-4"></div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div v-for="i in 5" :key="'sk-src-'+i" class="mb-3">
                <div class="flex justify-between mb-2">
                  <div class="h-3 w-40 bg-gray-200 rounded"></div>
                  <div class="h-3 w-10 bg-gray-200 rounded"></div>
                </div>
                <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-2 bg-gray-200 rounded-full" style="width: 50%"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="errorMsg" class="text-red-600 text-sm">
        Failed to load analytics. {{ errorMsg }}
        <button @click="load" class="ml-2 underline">Retry</button>
      </div>
      <div v-else-if="stats">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Unique clip views</div>
            <div class="text-3xl font-semibold">{{ stats.unique_views || 0 }}</div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Total clip views</div>
            <div class="text-3xl font-semibold">{{ stats.total_views || 0 }}</div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Total downloads</div>
            <div class="text-3xl font-semibold">{{ stats.total_downloads || 0 }}</div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Clips shared</div>
            <div class="text-3xl font-semibold">{{ stats.unique_clips_shared || 0 }}</div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Sessions</div>
            <div class="text-3xl font-semibold">{{ stats.sessions || 0 }}</div>
          </div>
          <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
            <div class="text-gray-500 text-sm">Page views</div>
            <div class="text-3xl font-semibold">{{ stats.page_views || 0 }}</div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Share distribution -->
          <div>
            <h3 class="text-lg font-semibold mb-3">Share distribution</h3>
            <div v-if="(stats.share_platforms || []).length === 0" class="text-sm text-gray-500">No share data in range.</div>
            <div v-else>
              <div v-for="row in stats.share_platforms" :key="row.platform" class="mb-3">
                <div class="flex justify-between text-sm mb-1"><span class="capitalize">{{ row.platform }}</span><span>{{ row.percent }}%</span></div>
                <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: row.percent + '%' }"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Geo -->
          <div>
            <h3 class="text-lg font-semibold mb-3">Timezones</h3>
            <div class="text-sm text-gray-600 mb-2">Timezones reached: <span class="font-semibold">{{ stats.timezones_reached || 0 }}</span></div>
            <div v-if="(stats.top_timezones || []).length === 0" class="text-sm text-gray-500">No timezone data in range.</div>
            <div v-else>
              <div v-for="row in stats.top_timezones" :key="row.timezone" class="mb-3">
                <div class="flex justify-between text-sm mb-1"><span>{{ row.timezone }}</span><span>{{ row.count }}</span></div>
                <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-2 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#A0C5FF]" :style="{ width: geoWidth(row.count) }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Top sources -->
        <div class="mt-8">
          <h3 class="text-lg font-semibold mb-3">Top sources</h3>
          <div v-if="(stats.top_sources || []).length === 0" class="text-sm text-gray-500">No referrer data in range.</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div v-for="row in stats.top_sources" :key="row.source" class="mb-3">
                <div class="flex justify-between text-sm mb-1"><span>{{ row.source }}</span><span>{{ row.percent }}%</span></div>
                <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#D1FAE5]" :style="{ width: row.percent + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

function decodeJwt(token) {
  try {
    const part = token.split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decodeURIComponent(escape(json)))
  } catch (e) { return {} }
}

export default {
  name: 'AnalyticsDashboard',
  data() {
    const token = localStorage.getItem('token') || ''
    const payload = token ? decodeJwt(token) : {}
    const roles = Array.isArray(payload?.roles) ? payload.roles : []
    const universities = Array.isArray(payload?.universities) ? payload.universities : []
    return {
      authorized: roles.includes('admin') || roles.includes('analytics-viewer') || roles.includes('analytics_viewer'),
      universities,
      selectedSlug: universities[0] || '',
      liveStats: null,
      liveLoading: false,
      liveError: '',
      liveTimer: null,
      allowedUniversities: [],
      start: null,
      end: null,
      activePreset: '7d',
      toggleCustom: false,
      customFrom: '',
      customTo: '',
      presets: [
        { key: '24h', label: 'Last 24h', deltaMs: 24*60*60*1000 },
        { key: '7d', label: 'Last 7 days', deltaMs: 7*24*60*60*1000 },
        { key: '30d', label: 'Last 30 days', deltaMs: 30*24*60*60*1000 },
        { key: '90d', label: 'Last 90 days', deltaMs: 90*24*60*60*1000 },
      ],
      stats: null,
      errorMsg: '',
      loading: false,
      // Live windows (in seconds)
      liveWindows: [30, 60, 300, 900],
      activeLiveWindow: 30,
    }
  },
  computed: {
    isAllScope() {
      return this.selectedSlug === '_ALL_'
    }
  },
  methods: {
    formatWindow(w) {
      if (w < 60) return `${w}s`
      if (w % 60 === 0 && w < 3600) return `${w/60}m`
      return `${(w/3600).toFixed(1)}h`
    },
    setLiveWindow(w) {
      this.activeLiveWindow = w
      this.loadLive()
    },
    geoWidth(count) {
      const max = Math.max(...(this.stats.top_timezones || []).map(r => r.count), 1)
      return (100 * count / max).toFixed(2) + '%'
    },
    onUniversityChange() {
      // Refresh panels according to their scope support
      if (this.selectedSlug === '_ALL_') {
        // Historical panels do not reflect All; clear their state
        this.stats = null
        this.errorMsg = ''
        this.loading = false
      } else {
        this.load()
      }
      this.loadLive()
    },
    async fetchUniversities() {
      // Fetch full list to map slugs -> names; filter to allowed slugs from JWT
      try {
        const r = await axios.get('/universities')
        const list = Array.isArray(r.data) ? r.data : []
        const allowed = new Set(this.universities)
        this.allowedUniversities = list.filter(u => allowed.has(u.slug))
        if (!this.selectedSlug && this.allowedUniversities.length) {
          this.selectedSlug = this.allowedUniversities[0].slug
        }
      } catch (e) {
        // Fallback: just use the slug from token
        if (!this.selectedSlug && this.universities && this.universities.length) {
          this.selectedSlug = this.universities[0]
        }
        this.allowedUniversities = (this.universities || []).map(s => ({ slug: s, name: s }))
      }
    },
    applyPreset(key) {
      this.activePreset = key
      this.toggleCustom = false
      const p = this.presets.find(x => x.key === key)
      if (!p) return
      const now = Date.now()
      this.start = now - p.deltaMs
      this.end = now
      if (!this.isAllScope) {
        this.load()
      } else {
        this.stats = null
        this.errorMsg = ''
        this.loading = false
      }
    },
    applyCustom() {
      if (this.customFrom) this.start = new Date(this.customFrom).getTime()
      if (this.customTo) this.end = new Date(this.customTo).getTime()
      this.activePreset = ''
      if (!this.isAllScope) {
        this.load()
      } else {
        this.stats = null
        this.errorMsg = ''
        this.loading = false
      }
    },
    async load() {
      if (!this.authorized) return
      // Historical panels are per-university only; if All is selected, do not load
      if (this.selectedSlug === '_ALL_') {
        this.stats = null
        this.errorMsg = ''
        this.loading = false
        return
      }
      this.loading = true
      try {
        this.errorMsg = ''
        this.stats = null
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const body = {}
        if (this.start) body.start = this.start
        if (this.end) body.end = this.end
        const url = base + '/stats/university'
        const payload = { ...body, universitySlug: this.selectedSlug }
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const r = await axios.post(url, payload, { headers })
        this.stats = r.data
      } catch (e) {
        console.error(e)
        this.stats = null
        try { this.errorMsg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.errorMsg = 'Unexpected error' }
      } finally {
        this.loading = false
      }
    },
    async loadLive() {
      if (!this.authorized) return
      this.liveLoading = this.liveStats ? false : true
      try {
        this.liveError = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/live/summary'
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const payload = { windowSeconds: this.activeLiveWindow }
        if (this.selectedSlug && this.selectedSlug !== '_ALL_') payload.universitySlug = this.selectedSlug
        const r = await axios.post(url, payload, { headers })
        this.liveStats = r.data
      } catch (e) {
        try { this.liveError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.liveError = 'Unexpected error' }
      } finally {
        this.liveLoading = false
      }
    },
    startLiveAutoRefresh() {
      if (this.liveTimer) clearInterval(this.liveTimer)
      this.loadLive()
      this.liveTimer = setInterval(this.loadLive, 5000)
    },
    stopLiveAutoRefresh() {
      if (this.liveTimer) clearInterval(this.liveTimer)
      this.liveTimer = null
    }
  },
  async mounted() {
    await this.fetchUniversities()
    this.applyPreset(this.activePreset)
    this.startLiveAutoRefresh()
  },
  beforeDestroy() {
    this.stopLiveAutoRefresh()
  }
}
</script>

<style scoped>
</style>
